import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

export interface FormattingImageProps {
  isImageSelectedInComposer: boolean;
  onInsertImageUrlInlineClick: () => void;
  onUploadImageInlineClick: () => void;
}

/*
 * Component.
 */

export const FormattingImage: FC<FormattingImageProps> = (props) => (
  <PopoverCoordinator
    renderButton={(isOpen) => renderButton(isOpen, props.isImageSelectedInComposer)}
    renderModal={(modalProps) => renderDropdown(modalProps, props)}
  />
);

/*
 * Button.
 */

function renderButton(isOpen: boolean, isActive: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          noMargin
          iconName="formattingImage"
          hasHover={isOpen}
          isOpen={isOpen}
          isActive={isActive}
          label={<Trans id="formatting-image">Insert image inline</Trans>}
          shouldPreventMouseDown
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(modalProps: PopoverTransferProps, props: FormattingImageProps) {
  return <SimpleDropdown {...modalProps}>{renderFormattingImageDropdownItems(props)}</SimpleDropdown>;
}

interface FormattingImageDropdownProps {
  onInsertImageUrlInlineClick: FormattingImageProps['onInsertImageUrlInlineClick'];
  isImageSelectedInComposer: FormattingImageProps['isImageSelectedInComposer'];
  onUploadImageInlineClick: FormattingImageProps['onUploadImageInlineClick'];
}

export function renderFormattingImageDropdownItems(props: FormattingImageDropdownProps) {
  return (
    <>
      <SimpleDropdownItem onSelect={props.onInsertImageUrlInlineClick}>
        {renderImageFromUrlLabel(props.isImageSelectedInComposer)}
      </SimpleDropdownItem>
      <SimpleDropdownItem onSelect={props.onUploadImageInlineClick}>
        {renderImageFromComputerLabel(props.isImageSelectedInComposer)}
      </SimpleDropdownItem>
    </>
  );
}

/*
 * Labels.
 */

function renderImageFromUrlLabel(isImageSelectedInComposer: boolean) {
  if (isImageSelectedInComposer) {
    return <Trans id="formatting-image-editurl">Edit image URL</Trans>;
  }

  return <Trans id="formatting-image-inserturl">Insert image from URL</Trans>;
}

function renderImageFromComputerLabel(isImageSelectedInComposer: boolean) {
  if (isImageSelectedInComposer) {
    return <Trans id="formatting-image-editcomputer">Replace image from computer</Trans>;
  }

  return <Trans id="formatting-image-insertcomputer">Insert image from computer</Trans>;
}
